import React, { useEffect, useMemo, useState } from "react"
import { css } from "@emotion/react"
import { Redirect, useParams } from "react-router-dom"
import {
	calculateListPriceFromDiscount,
	shouldShowClearancePrice,
	ButtonEcomm,
	Icon,
	PartClearancePriceEcomm,
	PartImage,
	Price,
	TrackingEvent,
} from "@ncs/web-legos"
import {
	CustomerPart,
	isNonParentCustomerPart,
	NonParentCustomerPart,
	useOnlinePartEcommService,
	useOnlinePartDetailsEcommService,
	PublishedAsset,
	useFrequentlyBoughtTogether,
} from "@ncs/ncs-api"
import {
	Box,
	Button,
	Divider,
	EventTracker,
	encodeUrlState,
	Heading,
	Link,
	Paragraph,
	PDPPriceEcomm,
	useChangeCallback,
	useScrollToTop,
	useUrlState,
} from "@ncs/web-legos"
import { useShopContext } from "~/contexts"
import { AddedToCartModalEcomm, PageContentWrapper, QuantityControl } from "~/shared-components"
import { PartChildSelectorEcomm } from "./components"
import PartDetailTabs from "./components/TechnicalDescription/PartDetails"
import {
	styleGoToCart,
	stylePartContainer,
	styleAddToCardContainer,
	styleAddToCardLabel,
	styleQuantityControl,
	styleInStock,
	styleAlternatePartNumber,
	styleAlternatePartNumbersContainer,
	alternatePartNumbersWrapper,
	partNumberWrapper,
	styleDetailsContainer,
	styleOnlinePartNumber,
	styleContainerOnlinePartNumber,
	styleBrandName,
	stylePartTitle,
	clearanceTag,
	priceContainer,
	partDetailWrapper,
	stylePartImage,
	quantitySelectCss,
	cardFooter,
	frequentlyBoughtTogetherCSS,
	frequentlyBoughtTogetherImageCss,
	partsGrid,
	partCard,
} from "./styles"
import { useAnalytics } from "~/hooks/useAnalytics"
import MultiImages from "./components/MultiImages"
import { ExpandableText } from "~/shared-components/expandable-text"
import { useExperiment } from "~/hooks/useExperiment"
import { underlineHoverStyle } from "~/shared-components/quantity-control/styles"

type PartDetailEcommUrlState = {
	childId: string | null
}

const EXPERIMENT = {
	FLAG: "pdp_add_to_cart_experiment",
	VARIANTS: {
		DEFAULT: "default",
		FLOATING: "floating",
	},
}

export const PartDetail: React.FC = () => {
	const scrollToTop = useScrollToTop()
	const { partId } = useParams<{ partId: string }>()
	// The part that we're immediately pointed to via the ID in the URL.
	const [initialPart, loadingInitialPart, { query }] = useOnlinePartEcommService(partId)
	const [partDetails, loadingPartDetails] = useOnlinePartDetailsEcommService(partId)
	const [localQuantity, setLocalQuantity] = useState(1)
	const [shopState, shopDispatch] = useShopContext()
	const [showAddedToCartModal, setShowAddedToCartModal] = useState(false)
	const variant = useExperiment(EXPERIMENT.FLAG) ?? EXPERIMENT.VARIANTS.DEFAULT

	const { trackEvent } = useAnalytics()

	// Frequently bought together
	const [frequentlyQuantity, setFrequentlyQuantities] = useState<Record<string, number>>({})

	const trackAddToCart = (event: string, part: any, quantity: number) => {
		trackEvent(event, {
			id: part.id,
			item_value: part.price,
			quantity,
			name: part.part?.description,
			part_title_name: part.title,
			part_number: part.onlinePartNumber,
		})
	}

	const handleQuantityChange = (id: string, value: number) => {
		setFrequentlyQuantities((prev) => ({
			...prev,
			[id]: value,
		}))
	}

	const unitOfMeasure = partDetails?.part?.unitOfMeasure
	const [frequentlyBoughtTogetherList] = useFrequentlyBoughtTogether(partId)
	const [lastAddedPartId, setLastAddedPartId] = useState<string | null>(null)

	const manufacturer = (partDetails?.manufacturers ?? [])
		.map((i) => i.manufacturerName)
		.join(", ")

	const brandName = useMemo(
		() => partDetails?.brands.map((brand) => brand.brandName).join(", "),
		[partDetails]
	)
	// Store the selected child's ID.
	const [{ childId: selectedChildId }, { updateUrlValue }] =
		useUrlState<PartDetailEcommUrlState>({
			childId: null,
		})
	const [selectedPartDetails] = useOnlinePartDetailsEcommService(selectedChildId)
	// Fetch the full CustomerPart for the selected child.
	const [selectedChildPart, loadingChildPartRaw] = useOnlinePartEcommService(selectedChildId)
	// Loading state of child part should include waiting for its parent too.
	const loadingChildPart = loadingInitialPart || loadingChildPartRaw

	/** This is the actual part we'd add to the cart after figuring out if it's
	 * a parent or not, and if so, selecting the child. */
	const selectedPart: NonParentCustomerPart | null = useMemo(() => {
		if (selectedChildPart && isNonParentCustomerPart(selectedChildPart)) {
			return selectedChildPart
		}
		if (!!initialPart && !initialPart.isParent && isNonParentCustomerPart(initialPart)) {
			return initialPart
		}

		return null
	}, [initialPart, selectedChildPart])

	const [assetsToRender, setAssetsToRender] = useState<PublishedAsset[]>([])

	useEffect(() => {
		if (partDetails) {
			const imageUrl = partDetails?.imageUrl
			const coverAsset =
				imageUrl ?
					[
						{
							assetUrl: imageUrl,
							description: partDetails?.title || "",
							assetName: "Cover Image",
							assetRole: "IMAGE",
							assetExtensionType: "jpg",
							id: 1,
							thumbnailDisplayOrdering: "1",
							metadata: undefined,
						},
					]
				:	[]
			const rotationalAsset =
				partDetails?.assets?.filter((image) => image.assetRole === "360") ?? []
			const publishedAssetsList =
				partDetails?.assets?.filter((image) => image.assetRole !== "360") ?? []
			const assets = [...coverAsset, ...rotationalAsset, ...publishedAssetsList]
			setAssetsToRender(assets as PublishedAsset[])
		}
	}, [partDetails])

	const alreadyInCartTally = useMemo(() => {
		return shopState.cart.reduce(
			(
				prev: number,
				{ part: partInCart, quantity }: { part: CustomerPart; quantity: number }
			) => (partInCart.id === selectedPart?.id ? prev + quantity : prev),
			0
		)
	}, [selectedPart, shopState.cart])

	// Add to cart PDP
	const handleAddToCart = (
		part: NonParentCustomerPart | CustomerPart,
		options?: {
			quantity?: number
			source?: string
		}
	) => {
		const quantity = options?.quantity ?? 1
		const source = options?.source ?? "PDP"

		if (quantity < 1) return

		shopDispatch({
			type: "add part to cart",
			payload: {
				part: part as NonParentCustomerPart,
				quantity,
			},
		})
		setLastAddedPartId(part.id)
		setShowAddedToCartModal(true)
		let event = TrackingEvent.PDP_ADD_TO_CART
		if (source === "PDP") {
			setLocalQuantity(1)
		} else {
			event = TrackingEvent.FREQUENTLY_BOUGHT_TOGETHER_ADD_TO_CART
		}

		trackAddToCart(event, part, quantity)
	}

	useEffect(() => {
		if (partDetails && partDetails.children) {
			const parts = [...partDetails.children]

			if (parts.length > 0 && !selectedChildId) {
				updateUrlValue("childId", parts[0]?.id.toString())
			}
		}
	}, [partDetails, selectedChildId, updateUrlValue])

	// Add the part to the Recent Parts list.
	useChangeCallback(initialPart, (part) => {
		if (part && !part.isChild) {
			shopDispatch({
				type: "add part to recent parts",
				payload: part.id,
			})
		}
	})

	useEffect(() => {
		scrollToTop()
	}, [partId, scrollToTop])

	// If we came to the page with the ID of a child part in the URL, we should
	// redirect to its parent.
	if (initialPart?.isChild && partDetails?.parentId) {
		return (
			<Redirect
				to={`/shop/parts/${partDetails.parentId}${encodeUrlState<PartDetailEcommUrlState>({
					childId: initialPart.id,
				})}`}
			/>
		)
	}

	const alternatePartNumbers =
		selectedPartDetails?.alternatePartNumbers ?? partDetails?.alternatePartNumbers ?? []

	return (
		<PageContentWrapper
			title={initialPart?.title}
			breadcrumbs={[
				{ name: "Shop", to: "/shop" },
				{ name: "Search", to: "/shop/search" },
				{ name: initialPart?.title ?? "" },
			]}
			showNotFound={query.status === "error"}
		>
			<div css={partDetailWrapper}>
				<div css={stylePartContainer}>
					<Box
						css={css`
							max-width: 384px;
							width: 100%;
						`}
					>
						{shouldShowClearancePrice(selectedPart) && (
							<Box display="flex" flexDirection="row" css={clearanceTag}>
								<Icon icon="tag" width={1.25} height={1.25} />
								<span>{selectedPart?.clearanceItemDiscount}% Off</span>
							</Box>
						)}

						{!loadingPartDetails && assetsToRender.length === 0 && (
							<PartImage
								src={initialPart?.imageUrl ?? ""}
								css={stylePartImage}
								alt={initialPart?.title ?? ""}
							/>
						)}

						{assetsToRender.length > 0 && !loadingPartDetails && (
							<MultiImages
								assets={assetsToRender}
								isLoading={loadingPartDetails}
								partName={initialPart?.title ?? ""}
							/>
						)}
					</Box>

					<div css={styleDetailsContainer}>
						<Paragraph css={styleBrandName} isLoading={loadingPartDetails}>
							{brandName || manufacturer}
						</Paragraph>
						<Heading variant="h2" isLoading={loadingInitialPart} css={stylePartTitle}>
							{initialPart?.title}
						</Heading>
						<Box css={partNumberWrapper}>
							<div css={styleContainerOnlinePartNumber}>Part Number</div>
							<Paragraph isLoading={loadingChildPart} css={styleOnlinePartNumber}>
								{selectedPart?.onlinePartNumber}
							</Paragraph>
						</Box>

						{alternatePartNumbers.length > 0 && (
							<Box css={alternatePartNumbersWrapper}>
								<div
									css={css`
										min-width: 192px;
									`}
								>
									Replacement for
								</div>
								<div css={styleAlternatePartNumbersContainer}>
									{alternatePartNumbers.map((partNumber) => (
										<Paragraph
											isLoading={loadingChildPart}
											css={styleAlternatePartNumber}
											key={`alt_part_number_${partNumber ?? 0}`}
										>
											{partNumber ?? 0}
										</Paragraph>
									))}
								</div>
							</Box>
						)}

						<Box mt={2} mb={2}>
							<ExpandableText
								title="Description"
								text={initialPart?.description || ""}
								lines={2}
								useID={partId}
							/>
						</Box>
						{initialPart?.isParent && !!partDetails && (
							<PartChildSelectorEcomm
								parentPart={initialPart}
								childParts={partDetails.children}
								selectedChildId={selectedChildId}
								setSelectedChildId={(newId) => {
									if (newId) {
										updateUrlValue("childId", newId)
									}
								}}
								loadingPartDetails={loadingPartDetails}
							/>
						)}

						<Paragraph isLoading={loadingInitialPart} css={styleInStock}>
							In Stock
						</Paragraph>

						<div
							css={{
								display: "flex",
								flexWrap: "wrap",
								alignItems: "flex-end",
								gap: "0.75rem",
							}}
						>
							<QuantityControl
								value={localQuantity}
								unitOfMeasure={unitOfMeasure}
								onChange={setLocalQuantity}
								css={styleQuantityControl}
								useUpdateButton
								selectCss={quantitySelectCss}
							/>
							{variant === EXPERIMENT.VARIANTS.FLOATING && (
								<Button
									variant="primary-cta"
									width="162px"
									css={styleAddToCardContainer}
									iconFamily="solid"
									onClick={() => {
										if (!selectedPart) return

										handleAddToCart(selectedPart, {
											quantity: localQuantity,
											source: "PDP",
										})
									}}
									disabled={
										localQuantity < 1 ||
										loadingInitialPart ||
										loadingChildPart ||
										loadingPartDetails ||
										(initialPart?.isParent && !partDetails?.children.length)
									}
								>
									<span css={styleAddToCardLabel}>Add To Cart</span>
								</Button>
							)}
						</div>

						<div css={priceContainer}>
							{shouldShowClearancePrice(selectedPart) ?
								<>
									<PartClearancePriceEcomm
										titleSize="big"
										price={selectedPart?.price ?? 0}
										clearanceDiscount={
											selectedPart?.clearanceItemDiscount ?? 0
										}
									/>
									<PDPPriceEcomm
										hasClearanceDiscount={selectedPart?.clearanceItem}
										price={calculateListPriceFromDiscount(selectedPart)}
										nonDiscountedPrice={calculateListPriceFromDiscount(
											selectedPart
										)}
										isLoading={
											loadingInitialPart ||
											loadingChildPart ||
											loadingPartDetails
										}
									/>
								</>
							:	<PDPPriceEcomm
									price={selectedPart?.netPrice ?? ""}
									nonDiscountedPrice={selectedPart?.price}
									isLoading={
										loadingInitialPart ||
										loadingChildPart ||
										loadingPartDetails
									}
								/>
							}
						</div>

						{variant === EXPERIMENT.VARIANTS.DEFAULT && (
							<Box mt={2} mb={3}>
								<Button
									variant="primary-cta"
									width="162px"
									css={styleAddToCardContainer}
									iconFamily="solid"
									onClick={() => {
										if (!selectedPart) return

										handleAddToCart(selectedPart, {
											quantity: localQuantity,
											source: "PDP",
										})
									}}
									disabled={
										localQuantity < 1 ||
										loadingInitialPart ||
										loadingChildPart ||
										loadingPartDetails ||
										(initialPart?.isParent && !partDetails?.children.length)
									}
								>
									<span css={styleAddToCardLabel}>Add To Cart</span>
								</Button>
							</Box>
						)}

						{alreadyInCartTally > 0 && (
							<div>
								<Paragraph display="inline-block" mr={0.5} mb={2}>
									You currently have {alreadyInCartTally} of this item in your
									cart.
								</Paragraph>
								<EventTracker
									events={[
										TrackingEvent.PDP_GO_TO_CART,
										TrackingEvent.GO_TO_CART,
									]}
									trackEvent={trackEvent}
								>
									<Link
										to="/shop/cart-summary"
										icon="shopping-cart"
										css={styleGoToCart}
									>
										Go to cart
									</Link>
								</EventTracker>
							</div>
						)}
					</div>
				</div>

				{/*	FREQUENTLY BOUGHT TOGETHER */}
				{frequentlyBoughtTogetherList && frequentlyBoughtTogetherList.length > 0 && (
					<div css={frequentlyBoughtTogetherCSS}>
						<h3>Frequently Bought Together</h3>
						<div css={partsGrid}>
							{frequentlyBoughtTogetherList.map((relatedPart) => (
								<div key={relatedPart.id} css={partCard}>
									<Link
										to={`/shop/parts/${relatedPart.id}`}
										css={underlineHoverStyle}
									>
										<EventTracker
											events={[
												TrackingEvent.FREQUENTLY_BOUGHT_TOGETHER_CLICKED_PRODUCT,
											]}
											trackEvent={trackEvent}
										>
											<div>
												<PartImage
													showBorder
													src={relatedPart.imageUrl}
													imageCss={frequentlyBoughtTogetherImageCss}
												/>
												<h4>{relatedPart?.title}</h4>
												<p className="onlinePartNumber">
													#{relatedPart.onlinePartNumber}
												</p>
											</div>
										</EventTracker>
									</Link>
									<div css={cardFooter}>
										<div>
											<Price price={relatedPart.netPrice} small />
										</div>
										<div>
											<QuantityControl
												value={frequentlyQuantity[relatedPart.id] ?? 1}
												unitOfMeasure={unitOfMeasure}
												onChange={(value) =>
													handleQuantityChange(relatedPart.id, value)
												}
												unitAtTop
												useUpdateButton
											/>
											<ButtonEcomm
												variant="secondary-cta"
												icon="cart-circle-plus"
												onClick={() =>
													handleAddToCart(relatedPart, {
														quantity:
															frequentlyQuantity[relatedPart.id] ??
															1,
														source: "frequentlyBoughtTogether",
													})
												}
											/>
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				)}

				<Divider mt={4} mb={4} />

				<PartDetailTabs
					partDetails={partDetails}
					initialPart={initialPart}
					selectedChildPart={selectedChildPart}
					selectedPartDetails={selectedPartDetails}
				/>

				<Divider mt={4} />

				<AddedToCartModalEcomm
					isOpen={showAddedToCartModal}
					partId={lastAddedPartId}
					onClose={() => setShowAddedToCartModal(false)}
				/>
			</div>
		</PageContentWrapper>
	)
}
